import React, { useState } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    TextField,
    Button,
    Checkbox,
    TableSortLabel,
    Box,
    Typography,
    CircularProgress,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DescriptionIcon from '@mui/icons-material/Description';
import PrintIcon from '@mui/icons-material/Print';
import ky from 'ky';
import { NotificationManager } from 'react-notifications';
import { appInsights } from '../../../services/appInisights';

const printHost = "http://localhost:7000";

const PrintBulkOrdersQR = () => {
    const [data, setData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [orderBy, setOrderBy] = useState('orderNumber');
    const [order, setOrder] = useState('asc');
    const [printing, setPrinting] = useState(false);
    const [fileSummary, setFileSummary] = useState(null);

    const rowsPerPageOptions = [20, 50, 100, 150, 200, 400, 600, 800, 1000];

    const handleFileUpload = (event) => {
        setFileSummary("");
        const file = event.target.files[0];
        if (!file) return;

        // Extract formatted summary from filename
        const fileName = file.name.replace(".txt", "");
        const match = fileName.match(
            /^BulkOrders_Account_(\d+)_(\d{1,2})_(\w+)_(\d{4})_(\d{1,2})_(\d{1,2})_(\d{1,2})/
        );

        if (match) {
            const [_, account, day, month, year, hour, min, sec] = match;
            const formatted = `Bulk orders created for the account ${account} on ${day} ${month} ${year} at ${hour}:${min}:${sec}`;
            setFileSummary(formatted);
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            const content = e.target.result;
            parseZLPData(content);
        };
        reader.readAsText(file);
    };


    const parseZLPData = (content) => {
        const zlpBlocks = content.split(/\n\s*\n\s*\n+/).filter(block => block.trim());

        const parsedData = zlpBlocks.map((block, index) => {
            const orderNumberMatch = block.match(/Order Number:\s*(\d+)/);
            const orderNumber = orderNumberMatch ? orderNumberMatch[1] : '';

            const webLinkMatch = block.match(/Web Link:\s*(https?:\/\/[^\s^]+)/);
            const webLink = webLinkMatch ? webLinkMatch[1] : '';

            const labelTypeMatch = block.match(/\^FD(\d+):\s*([^\s^]+)/);
            const labelType = labelTypeMatch ? labelTypeMatch[2] : '';

            return {
                id: index + 1,
                orderNumber,
                webLink,
                labelType,
                selected: true,
                zlpCode: block.trim()
            };
        }).filter(item => item.orderNumber);

        setData(parsedData);
        setPage(0);
    };

    const GetPrinters = async () => {
        try {
            let response = await ky.get(`${printHost}/printers/list`);

            if (response.status !== 200) {
                NotificationManager.error("Error getting printers from PrintIt");
                return [];
            }

            let printers = await response.json();
            return printers;
        } catch (err) {
            NotificationManager.error("Error getting printers");
            throw new Error("Error getting printers");
        }
    };

    const GetPrinterMappings = async () => {
        let printers = await GetPrinters();
        let mappings = [];

        var zebraPrinters = printers.filter((p) =>
            p.name.toLowerCase().includes("zdesigner")
        );

        zebraPrinters.map((printer) =>
            mappings.push({
                name: "Zebra",
                printerName: printer.name,
                type: "Zebra",
                isActive: printer.isActive,
            })
        );

        return mappings;
    };

    const GetMappingForLabel = (labelType, labelName, mappings) => {
        var mapping = mappings.filter(function (m) {
            return (
                m.type === labelType &&
                m.printerName.toLowerCase().includes(labelName.toLowerCase().trim())
            );
        })[0];

        if (!mapping) {
            mapping = mappings.filter(function (m) {
                return m.type === labelType;
            })[0];
        }

        if (!mapping) {
            NotificationManager.error("No mapping for this label: " + labelType);
            return null;
        }

        return mapping;
    };

    const handlePrintSelected = async () => {
        const selectedItems = data.filter(item => item.selected);

        if (selectedItems.length === 0) {
            NotificationManager.warning("No orders selected for printing");
            return;
        }

        setPrinting(true);
        let successCount = 0;
        let failCount = 0;

        try {
            // Get printer mappings once
            let printerMappings = await GetPrinterMappings();
            let printerMapping = GetMappingForLabel("Zebra", "orderBarcode", printerMappings);

            if (!printerMapping) {
                NotificationManager.error("No Zebra printer found");
                setPrinting(false);
                return;
            }

            // Print each label sequentially
            for (let i = 0; i < selectedItems.length; i++) {
                const item = selectedItems[i];

                try {
                    let printResponse = await ky.post(`${printHost}/print/label`, {
                        json: {
                            Base64Xml: btoa(item.zlpCode),
                            PrinterName: printerMapping.printerName,
                        },
                    });

                    if (printResponse.status !== 200) {
                        throw new Error(
                            `Error printing barcode for order ${item.orderNumber}. Status code ${printResponse.status}`
                        );
                    }

                    successCount++;
                    console.log(`Order ${item.orderNumber} printed successfully (${i + 1}/${selectedItems.length})`);

                } catch (error) {
                    failCount++;
                    console.error(`Error printing order ${item.orderNumber}:`, error);

                    appInsights.trackEvent({
                        name: "Error Printing Order Barcode Label",
                        properties: {
                            message: `Error printing Order Barcode Label for order ${item.orderNumber}`,
                            error: error.message,
                        },
                    });
                }
            }

            // Show summary notification
            if (successCount > 0 && failCount === 0) {
                NotificationManager.success(`Successfully printed ${successCount} label(s)`);
            } else if (successCount > 0 && failCount > 0) {
                NotificationManager.warning(
                    `Printed ${successCount} label(s), ${failCount} failed`
                );
            } else {
                NotificationManager.error(`Failed to print all ${failCount} label(s)`);
            }

        } catch (error) {
            NotificationManager.error("Error during printing process");
            console.error("Print error:", error);

            appInsights.trackEvent({
                name: "Error Printing Batch Order Barcode Labels",
                properties: {
                    message: "Error printing batch Order Barcode Labels",
                    error: error.message,
                },
            });
        } finally {
            setPrinting(false);
        }
    };

    const handleSelectAll = (event) => {
        const checked = event.target.checked;
        setData(data.map(item => ({ ...item, selected: checked })));
    };

    const handleSelectItem = (id) => (event) => {
        const checked = event.target.checked;
        setData(data.map(item =>
            item.id === id ? { ...item, selected: checked } : item
        ));
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const getComparator = (order, orderBy) => {
        return order === 'desc'
            ? (a, b) => descendingComparator(a, b, orderBy)
            : (a, b) => -descendingComparator(a, b, orderBy);
    };

    const descendingComparator = (a, b, orderBy) => {
        if (orderBy === 'orderNumber') {
            return parseInt(b[orderBy]) - parseInt(a[orderBy]);
        }
        if (b[orderBy] < a[orderBy]) {
            return -1;
        }
        if (b[orderBy] > a[orderBy]) {
            return 1;
        }
        return 0;
    };

    const stableSort = (array, comparator) => {
        const stabilizedThis = array.map((el, index) => [el, index]);
        stabilizedThis.sort((a, b) => {
            const order = comparator(a[0], b[0]);
            if (order !== 0) return order;
            return a[1] - b[1];
        });
        return stabilizedThis.map((el) => el[0]);
    };

    const filteredData = data.filter(item =>
        item.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.labelType.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.webLink.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const sortedData = stableSort(filteredData, getComparator(order, orderBy));
    const paginatedData = sortedData.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    const selectedCount = data.filter(item => item.selected).length;
    const allSelected = data.length > 0 && data.every(item => item.selected);
    const someSelected = data.some(item => item.selected) && !allSelected;

    return (
        <>
            <span className="title">Print Bulk Orders QR</span>
            <hr />
            <Paper elevation={3} sx={{ padding: 3 }}>
                {data.length === 0 ? (
                    <>
                        <Box
                            sx={{
                                border: '2px dashed',
                                borderColor: 'grey.300',
                                borderRadius: 2,
                                p: 6,
                                textAlign: 'center',
                            }}
                        >
                            <DescriptionIcon sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />

                            <Button
                                variant="contained"
                                component="label"
                                startIcon={<UploadFileIcon />}
                                sx={{ mb: 2 }}
                            >
                                Select ZLP Text File
                                <input
                                    type="file"
                                    accept=".txt"
                                    hidden
                                    onChange={handleFileUpload}
                                />
                            </Button>

                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Upload the <strong>ZLP Text File</strong> generated during the bulk order process.
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                These files are automatically created by the system and saved in the shared drive:
                                <br />
                                <strong>\\ENVLAB-FILE01\vdrive\BulkQRLabelImages</strong>
                                <br /><br />
                                Locate the folder for the <strong>specific client</strong> you created bulk orders for,
                                then upload the matching ZLP file here to load and print their QR labels.
                            </Typography>

                        </Box>

                        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                            <Button
                                variant="outlined"
                                color="error"
                                size="large"
                                onClick={() => (window.location.href = "/vlp/new-bulk-order")}
                            >
                                Cancel
                            </Button>
                        </Box>

                    </>
                ) : (
                    <>
                        {fileSummary && (
                            <Typography
                                variant="subtitle1"
                                sx={{ mb: 2, fontWeight: 500, color: 'text.primary' }}
                            >
                                {fileSummary}
                            </Typography>
                        )}

                        <Box sx={{ display: 'flex', mb: 2, alignItems: 'center', gap: 2 }}>
                            <TextField
                                label="Search"
                                variant="outlined"
                                size="small"
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setPage(0);
                                }}
                                sx={{ flexGrow: 1 }}
                            />
                            <Button
                                variant="contained"
                                component="label"
                                startIcon={<UploadFileIcon />}
                            >
                                New File
                                <input
                                    type="file"
                                    accept=".txt"
                                    hidden
                                    onChange={handleFileUpload}
                                />
                            </Button>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                <strong>{selectedCount}</strong> of <strong>{data.length}</strong> orders selected
                                {searchTerm && (
                                    <span style={{ marginLeft: '16px' }}>
                                        • Showing <strong>{filteredData.length}</strong> filtered results
                                    </span>
                                )}
                            </Typography>

                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={printing ? <CircularProgress size={20} color="inherit" /> : <PrintIcon />}
                                onClick={handlePrintSelected}
                                disabled={printing || selectedCount === 0}
                            >
                                {printing ? 'Printing...' : 'Print Barcodes'}
                            </Button>
                        </Box>

                        <TableContainer sx={{ maxHeight: 460, overflowY: 'auto' }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={allSelected}
                                                indeterminate={someSelected}
                                                onChange={handleSelectAll}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === 'orderNumber'}
                                                direction={orderBy === 'orderNumber' ? order : 'asc'}
                                                onClick={() => handleRequestSort('orderNumber')}
                                            >
                                                Order Number
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === 'labelType'}
                                                direction={orderBy === 'labelType' ? order : 'asc'}
                                                onClick={() => handleRequestSort('labelType')}
                                            >
                                                Label Type
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === 'webLink'}
                                                direction={orderBy === 'webLink' ? order : 'asc'}
                                                onClick={() => handleRequestSort('webLink')}
                                            >
                                                Web Link
                                            </TableSortLabel>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedData.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                                <Typography color="text.secondary">
                                                    No results found
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        paginatedData.map((item, index) => (
                                            <TableRow
                                                key={item.id}
                                                sx={{
                                                    backgroundColor:
                                                        index % 2 === 0 ? 'rgba(0, 0, 0, 0.04)' : 'white',
                                                }}
                                            >
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={item.selected}
                                                        onChange={handleSelectItem(item.id)}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <span style={{ color: '#1976d2', fontWeight: 500 }}>
                                                        {item.orderNumber}
                                                    </span>
                                                </TableCell>
                                                <TableCell>{item.labelType}</TableCell>
                                                <TableCell>
                                                    <a
                                                        href={item.webLink}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{ color: '#1976d2', textDecoration: 'none' }}
                                                    >
                                                        {item.webLink}
                                                    </a>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            rowsPerPageOptions={rowsPerPageOptions}
                            component="div"
                            count={filteredData.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                            <Button
                                variant="outlined"
                                color="error"
                                size="large"
                                onClick={() => (window.location.href = "/vlp/new-bulk-order")}
                            >
                                Cancel
                            </Button>
                        </Box>

                    </>
                )}
            </Paper>
        </>
    );
};

export default PrintBulkOrdersQR;